import { z } from 'zod/v3';

// Video type enum
export const VideoTypeDTOSchema = z.enum(['transitions', 'screen_recording']);

export const RecordingFrameworkRequirementDTOSchema = z.object({
  frameworkDefId: z.string().min(1),
  requirementRef: z.string().min(1),
});

export const RecordingNewTestDTOSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(4000),
  frameworkRequirements: z
    .array(RecordingFrameworkRequirementDTOSchema)
    .min(1),
});

export const RecordingFrameworkRequirementGroupDTOSchema = z.object({
  frameworkDefId: z.string(),
  frameworkKey: z.string(),
  frameworkSlug: z.string(),
  frameworkName: z.string(),
  requirements: z.array(
    z.object({
      requirementRef: z.string(),
      title: z.string(),
    }),
  ),
});

// Start recording schema
export const StartRecordingDTOSchema = z.object({
  startTime: z.string().optional(),
  sectionId: z.string().optional(),
  guidedTemplateId: z.string().optional(), // RFC-097: Guided template ID for flow association
  controlEvidenceStatusId: z.string().optional(), // Task/subtask ID for direct status updates on submit
  stepRunId: z.string().optional(), // Procedure-run StepRun to bind this recording to (human browser_recording field)
  stepFieldId: z.string().optional(), // Human-form field id this recording satisfies (paired with stepRunId)
  captureMode: z.enum(['tab', 'screen']).optional().default('tab'),
  enableAudio: z.boolean().optional().default(false), // Pre-create FlowMedia when audio recording is enabled
  // Screen video capture (tab or full screen). The FlowMedia is typed VIDEO and
  // its chunks route to the shared video pipeline instead of the audio one —
  // video subsumes narration, so this wins when both are set.
  enableVideo: z.boolean().optional().default(false),
  // #1315 Remedy in Browser: 'remedy' marks the Flow as a remediation audit
  // trail (RecordingPurpose.REMEDIATION) instead of evidence capture. Defaults
  // to evidence when omitted, preserving existing Capture-in-Browser behavior.
  completionMode: z.enum(['evidence', 'remedy']).optional(),
  // RemediationTicket this remedy recording is for (#1315). Only meaningful for
  // 'remedy' recordings launched from a finding.
  remediationTicketId: z.string().optional(),
});


// Save HTML snapshot schema
export const SaveHtmlSnapshotDTOSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  url: z.string(),
  html: z.string(),
  markdown: z.string().nullish(),
  timestamp: z.date().nullish(),
  relativeTime: z.number().nullish(),
  domFingerprint: z.string().nullish(),
  snapshotType: z.string().nullish(),
  domState: z.record(z.string(), z.any()).nullish(),
});

// DOM diff operation schema
export const DomDiffOperationDTOSchema = z.object({
  type: z.enum(['add', 'remove', 'modify']),
  path: z.array(z.string().or(z.number())),
  value: z.any().nullish(),
});

// Save DOM diff schema
export const SaveDomDiffDTOSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  stepId: z.string(),
  baseSnapshotId: z.string(),
  operations: z.array(DomDiffOperationDTOSchema),
  timestamp: z.date().nullish(),
  relativeTime: z.number().nullish(),
});

// End recording schema
export const EndRecordingDTOSchema = z.object({
  id: z.string(),
  endTime: z.date().nullish(),
  stepsCount: z.number(),
});


// WebSocket message schemas
export const StartMessageSchema = z.object({
  type: z.literal('start'),
  flowId: z.string(),
  workspaceId: z.string(),
  mimeType: z.string(),
});

export const ChunkMessageSchema = z.object({
  type: z.literal('chunk'),
  chunkIndex: z.number(),
  timestamp: z.number(), // ms relative to recording start
  // Binary payload will be sent as ArrayBuffer separate from JSON (ws binary frame)
});

export const FinalizeMessageSchema = z.object({
  type: z.literal('finalize'),
  duration: z.number(),
  segmentMarkers: z.array(z.number()),
  totalChunks: z.number(),
});

export const IncomingMessageSchema = z.discriminatedUnion('type', [
  StartMessageSchema,
  ChunkMessageSchema,
  FinalizeMessageSchema,
]);

// WebSocket response schemas
export const StartedResponseSchema = z.object({
  type: z.literal('started'),
  flowId: z.string(),
});

export const FinalizedResponseSchema = z.object({
  type: z.literal('finalized'),
  id: z.string(),
  url: z.string().nullable(),
  success: z.boolean(),
  processingJobId: z.string(),
  vttUrl: z.string().nullable().optional(),
  subtitlesReady: z.boolean(),
});

export const ErrorResponseSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
});

export const WebSocketResponseSchema = z.discriminatedUnion('type', [
  StartedResponseSchema,
  FinalizedResponseSchema,
  ErrorResponseSchema,
]);

// WebSocket data schema
export const WebSocketDataSchema = z.object({
  session: z.object({
    flowId: z.string(),
    workspaceId: z.string(),
    mimeType: z.string(),
    nextChunkIndex: z.number(),
  }),
});

// Export types
export type VideoTypeDTO = z.infer<typeof VideoTypeDTOSchema>;
export type RecordingFrameworkRequirementDTO = z.infer<
  typeof RecordingFrameworkRequirementDTOSchema
>;
export type RecordingNewTestDTO = z.infer<typeof RecordingNewTestDTOSchema>;
export type RecordingFrameworkRequirementGroupDTO = z.infer<
  typeof RecordingFrameworkRequirementGroupDTOSchema
>;
export type StartRecordingDTO = z.infer<typeof StartRecordingDTOSchema>;
export type SaveHtmlSnapshotDTO = z.infer<typeof SaveHtmlSnapshotDTOSchema>;
export type DomDiffOperationDTO = z.infer<typeof DomDiffOperationDTOSchema>;
export type SaveDomDiffDTO = z.infer<typeof SaveDomDiffDTOSchema>;
export type EndRecordingDTO = z.infer<typeof EndRecordingDTOSchema>;

// WebSocket types
export type StartMessage = z.infer<typeof StartMessageSchema>;
export type ChunkMessage = z.infer<typeof ChunkMessageSchema>;
export type FinalizeMessage = z.infer<typeof FinalizeMessageSchema>;
export type IncomingMessage = z.infer<typeof IncomingMessageSchema>;
export type StartedResponse = z.infer<typeof StartedResponseSchema>;
export type FinalizedResponse = z.infer<typeof FinalizedResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type WebSocketResponse = z.infer<typeof WebSocketResponseSchema>;
export type WebSocketData = z.infer<typeof WebSocketDataSchema>;
